import { useContext, useEffect, useState } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import AdHocCommands from "@/api/adHocCommands"
import { parseHybridJsonResponse } from "@/components/cmrReport/parseHybridJsonResponse"
import {
  GPS_DOCUMENT_LINKS_FLAG,
  isGpsDocumentLinksAccount,
} from "@/lib/gpsDocumentLinks"
import { XmppContext } from "@/stream/xmppClient"

export type GpsOrderDocument = {
  messageId: string
  fileUrl: string
  fileName: string
  documentType: string
  timestamp: string
  chatJid: string
}

type DocumentsByOrder = Record<string, GpsOrderDocument[]>

const NO_DOCUMENTS: DocumentsByOrder = {}

/** Documents GPS matched to the report's orders, keyed by delivery order ID. */
export const useGpsOrderDocuments = (orderIds: string[]): DocumentsByOrder => {
  const { client, myJid } = useContext(XmppContext)
  const enabled = isGpsDocumentLinksAccount(
    myJid?.getDomain(),
    useFeatureFlagEnabled(GPS_DOCUMENT_LINKS_FLAG),
  )
  const key = [...new Set(orderIds)].sort().join(",")
  const [loaded, setLoaded] = useState<{
    key: string
    documents: DocumentsByOrder
  }>()

  useEffect(() => {
    if (!enabled || !client || !key) return
    let current = true
    AdHocCommands.getGpsOrderDocuments(client, key.split(","))
      .then((result) =>
        parseHybridJsonResponse<{ documents?: DocumentsByOrder }>(result),
      )
      .then(({ documents = {} }) => {
        if (current) setLoaded({ key, documents })
      })
      .catch((error) => console.warn("Could not load order documents", error))
    return () => {
      current = false
    }
  }, [enabled, client, key])

  return loaded?.key === key ? loaded.documents : NO_DOCUMENTS
}
